import type { CustomFilterMainCreators } from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import React, { useContext, useRef } from 'react';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import {
  HorizontalLayoutContainer,
  HorizontalLayoutInnerContainer,
  StickyHeaderClipper,
} from '@/app/common/filters/surfaces/bar/HorizontalLayout/styled.ts';
import type { FilterPill } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import ScrollContainer, { type ScrollContainerHandle } from '@/app/common/components/ScrollContainer';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { VerticalSeparator } from '@/app/common/components/Separator';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import useIsSticky from '@/app/common/hooks/useIsSticky.ts';
import { HEADER_HEIGHT } from '@/app/common/constants.ts';
import { useHeaderHeight } from '@/app/common/headerFooter/headerHeight.ts';
import { StickyFirstPill, useDynamicFilterUpdate } from '@/app/common/filters/surfaces/bar/StickyFirstPill.tsx';
import { renderPillRow } from '@/app/common/filters/surfaces/bar/pills/PillRow.tsx';
import { sortPill } from '@/app/common/filters/surfaces/bar/resultsPills/barPills.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';

export type HorizontalLayoutProps = {
  filterPills: FilterPill[];
  customFilterMainCreators: CustomFilterMainCreators;
  isLoading: boolean;
  handleFilterChanged: HandleFilterChanged;
  isIndex?: boolean;
  disabled?: boolean;
};

const HorizontalLayout = ({
  filterPills,
  customFilterMainCreators,
  isLoading,
  handleFilterChanged,
  isIndex,
  disabled = false,
}: HorizontalLayoutProps) => {
  const { localization, commonData } = usePageWrapperData();
  const { isMobile, externalHeaderEnabled } = useCommonClient();
  const { brand } = localization;
  const stickyFilters = filterPills.slice(0, 1);
  const baseScrollableFilters = filterPills.slice(1);
  const { isApp } = commonData;
  const { useEvoFilters: evo } = useFiltersUiData();

  const scrollableFilters = isIndex ? [sortPill(), ...baseScrollableFilters] : baseScrollableFilters;
  const { listView } = useContext(SearchPageContext);
  const { sentinelRef, isSticky } = useIsSticky(useHeaderHeight(HEADER_HEIGHT(brand, isMobile)));
  const showShadow = !listView || isSticky;
  const isActivelySticky = !!listView && isSticky;

  const onDynamicFilterUpdate = useDynamicFilterUpdate();
  const scrollRef = useRef<ScrollContainerHandle>(null);

  // Only the evo bar moves an applied pill to the front, so only it has a reason to scroll back.
  const handleFilterChangedAtStart: HandleFilterChanged = (...args) => {
    if (evo) scrollRef.current?.scrollToStart();
    handleFilterChanged(...args);
  };

  const filterGroupsPills = renderPillRow({
    filterPills: scrollableFilters,
    customFilterMainCreators,
    isLoading,
    disabled,
    handleFilterChanged: handleFilterChangedAtStart,
    onDynamicFilterUpdate,
  });

  return (
    <>
      {listView && <div ref={sentinelRef} style={{ height: 0, width: 0 }} />}
      <HorizontalLayoutContainer
        brand={brand}
        className="horizontal-filters-container"
        mode={listView ? 'sticky' : 'fixed'}
        showShadow={showShadow}
        isActivelySticky={isActivelySticky}
        isApp={isApp}
        externalHeaderEnabled={externalHeaderEnabled}
        aria-busy={isLoading}
      >
        <HorizontalLayoutInnerContainer mode={listView ? 'sticky' : 'fixed'}>
          <StickyFirstPill
            filterPills={stickyFilters}
            customFilterMainCreators={customFilterMainCreators}
            isLoading={isLoading}
            disabled={disabled}
            handleFilterChanged={handleFilterChangedAtStart}
          />
          <VerticalSeparator height={24} marginLeft={8} marginRight={8} />
          <ScrollContainer
            ref={scrollRef}
            evo={evo}
            pointerOnlyArrows={true}
            styleInner={{ padding: '0 4px', marginLeft: 4 }}
          >
            {[...filterGroupsPills]}
          </ScrollContainer>
        </HorizontalLayoutInnerContainer>
      </HorizontalLayoutContainer>
      {isActivelySticky && <StickyHeaderClipper brand={brand} />}
    </>
  );
};

export default HorizontalLayout;
